import getSignInServerSideProps from '@/lib/serverSideProps/serverSidePropsPages/getSignInServerSideProps';
import { GetServerSideProps } from 'next';
import { ReactElement, useEffect, useState } from 'react';
import { ImagesAuthenticationSignInSignInBannerWebp } from '@/assets/images_authentication_sign-in.assets';

import {
  ImagesAuthenticationCommonAppleLoginSvg,
  ImagesAuthenticationCommonGoogleLoginSvg,
} from '@/assets/images_authentication_common.assets';

import Image from 'next/image';
import { IoMdEye, IoMdEyeOff } from 'react-icons/io';
import DefaultButton from '@/components/common/default/DefaultButton';
import { PrimaryButton } from '@/components/common/PrimaryButton';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { AuthenticationEnum } from '@/types/translations/AuthenticationEnum';
import DefaultLink from '@/components/common/default/DefaultLink';
import SignInMeta from '@/components/seo/meta/SignInMeta';
import { useRouter } from 'next/router';
import { toast } from 'react-toastify';
import AuthInput from '@/components/auth/AuthInput';
import AuthCommonLayout from '@/components/auth/AuthCommonLayout';
import { useGoogleCaptcha } from '@/context/GoogleCaptchaProvider';
import ReCaptchaDisclaimer from '@/components/common/RecaptchaDisclaimer';

export default function SignIn() {
  const { query, replace } = useRouter();
  const { returnTo, error } = query;
  const [captcha, setCaptcha] = useState('');
  const { translate } =
    useTypedTranslation<AuthenticationEnum>('authentication');
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { executeRecaptcha, isScriptLoaded } = useGoogleCaptcha();

  useEffect(() => {
    if (error && typeof window !== undefined) {
      if (error === 'Invalid email or password') {
        toast.error(translate(AuthenticationEnum.invalid_email_or_password));
      } else if (error === 'An error occurred') {
        toast.error(translate(AuthenticationEnum.an_unexpected_error_occurred));
      } else if (error === 'Captcha is required') {
        toast.error(translate(AuthenticationEnum.captcha_is_required));
      } else if (error === 'Invalid captcha') {
        toast.error(translate(AuthenticationEnum.invalid_captcha));
      } else if (error === 'Account blocked') {
        toast.error(
          translate(
            AuthenticationEnum.account_blocked_you_will_receive_an_email_with_instructions_on_how_to_unblock_it
          )
        );
      } else {
        toast.error(translate(AuthenticationEnum.an_unexpected_error_occurred));
      }
      replace('/auth/sign-in', undefined, { shallow: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (isScriptLoaded) {
      executeRecaptcha('sign_in').then((token) => {
        setCaptcha(token);
      });
    }

    return () => {
      setCaptcha('');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScriptLoaded]);

  return (
    <>
      <SignInMeta />
      <AuthCommonLayout
        bgImage={ImagesAuthenticationSignInSignInBannerWebp}
        coloredBg={false}
      >
        <div className="tw-w-full">
          <h1 className="tw-font-prata tw-font-normal tw-text-5xl tw-leading-[65.04px] tw-text-[#232323] max-nine:tw-text-center max-md:tw-text-3xl">
            {translate(AuthenticationEnum.sign_in)}
          </h1>
          <p className="tw-font-medium tw-text-base tw-leading-[20.16px] tw-font-plusJakartaSans tw-text-[#54606FCC] max-nine:tw-text-center">
            {translate(
              AuthenticationEnum.sign_in_to_enjoy_the_feature_of_spiritory
            )}
          </p>
          <form
            className="tw-contents"
            action={
              returnTo
                ? `/api/auth/sign-in/credentials?returnTo=${returnTo}`
                : '/api/auth/sign-in/credentials'
            }
            method="POST"
          >
            <input type="hidden" name="captcha" value={captcha} />

            <div className="tw-mt-3 tw-relative tw-flex tw-flex-col tw-items-start tw-w-full">
              <AuthInput
                name="email"
                type="email"
                label={translate(AuthenticationEnum.email)}
                placeholder={translate(AuthenticationEnum.email)}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                invalidMessage=""
              />
            </div>
            <div className="tw-relative tw-flex tw-flex-col tw-items-start tw-w-full">
              <div className="tw-relative tw-w-full">
                <AuthInput
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  label={translate(AuthenticationEnum.password)}
                  placeholder={translate(AuthenticationEnum.password)}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  invalidMessage=""
                />
                <DefaultButton
                  aria-label={
                    showPassword
                      ? translate(AuthenticationEnum.hide_password)
                      : translate(AuthenticationEnum.show_password)
                  }
                  className="tw-absolute tw-right-4 -tw-translate-y-2/4 tw-bottom-7 tw-cursor-pointer"
                >
                  {showPassword ? (
                    <IoMdEyeOff
                      className="tw-text-xl tw-text-[#9A9A9A]"
                      onClick={() => setShowPassword(false)}
                    />
                  ) : (
                    <IoMdEye
                      className="tw-text-xl tw-text-[#9A9A9A]"
                      onClick={() => setShowPassword(true)}
                    />
                  )}
                </DefaultButton>
              </div>
            </div>
            <div className="tw-flex tw-items-center tw-justify-end my-3">
              <DefaultLink
                href="/auth/forgot-password?returnTo=sign_in"
                className="tw-font-bold tw-text-main max-sm:tw-text-sm"
              >
                {translate(AuthenticationEnum.forgot_password)}
              </DefaultLink>
            </div>
            <div className="tw-mt-5 tw-w-full">
              <PrimaryButton
                type="submit"
                className="tw-w-full nine:tw-max-w-full tw-font-plusJakartaSans tw-font-bold tw-text-white tw-text-lg"
              >
                {translate(AuthenticationEnum.sign_in)}
              </PrimaryButton>
              <ReCaptchaDisclaimer />
            </div>
          </form>
          <div className="tw-flex tw-items-center tw-justify-center tw-my-4 tw-gap-3">
            <hr className="tw-bg-[#DEDFE2] tw-bg-opacity-100 tw-h-px tw-w-full" />
            <span className="tw-font-plusJakartaSans tw-font-medium tw-text-base tw-leading-[20.16px] tw-text-[#6E6E6E]">
              {translate(AuthenticationEnum.or)}
            </span>
            <hr className="tw-bg-[#DEDFE2] tw-bg-opacity-100 tw-h-px tw-w-full" />
          </div>
          <div className="tw-w-full">
            <form
              className="tw-w-full"
              action={`/api/auth/sign-in/social/google-oauth2`}
              method="POST"
            >
              <DefaultButton
                className="tw-text-lg  tw-w-full nine:tw-max-w-full tw-basis-full tw-border tw-border-solid tw-border-platinum-gray tw-rounded-xl tw-px-2 tw-py-3 tw-font-bold tw-font-plusJakartaSans tw-flex tw-items-center tw-gap-3 tw-text-nowrap tw-justify-center max-md:tw-w-full max-sm:tw-text-xs tw-text-[#232323] tw-transition-all tw-duration-200 hover:tw-bg-[#F4F4F6] hover:tw-border-[#C4C5C8] focus:tw-ring-2 focus:tw-ring-[#4285F4] focus:tw-border-[#4285F4] active:tw-bg-[#E0E0E2] active:tw-border-[#A0A1A4] disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                type="submit"
                disabled={!captcha}
              >
                {translate(AuthenticationEnum.continue_with_google)}
                <Image
                  src={ImagesAuthenticationCommonGoogleLoginSvg}
                  alt="Google login"
                  width={24}
                  height={24}
                />
              </DefaultButton>
              <input type="hidden" name="captcha" value={captcha} />
            </form>
          </div>
          <div className="tw-w-full tw-mt-4">
            <form
              className="tw-w-full"
              action={`/api/auth/sign-in/social/apple`}
              method="POST"
            >
              <DefaultButton
                className="tw-text-lg tw-w-full nine:tw-max-w-full tw-basis-full tw-border tw-border-solid tw-border-platinum-gray tw-rounded-xl tw-px-2 tw-py-3 tw-font-bold tw-font-plusJakartaSans tw-flex tw-items-center tw-gap-3 tw-text-nowrap tw-justify-center max-md:tw-w-full max-sm:tw-text-xs tw-text-[#232323] tw-transition-all tw-duration-200 hover:tw-bg-[#F4F4F6] hover:tw-border-[#C4C5C8] focus:tw-ring-2 focus:tw-ring-[#4285F4] focus:tw-border-[#4285F4] active:tw-bg-[#E0E0E2] active:tw-border-[#A0A1A4] disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                type="submit"
                disabled={!captcha}
              >
                {translate(AuthenticationEnum.continue_with_apple)}
                <Image
                  src={ImagesAuthenticationCommonAppleLoginSvg}
                  alt="Apple login"
                  width={24}
                  height={24}
                />
              </DefaultButton>
              <input type="hidden" name="captcha" value={captcha} />
            </form>
          </div>
          <div className="tw-mt-7 tw-flex tw-items-center tw-justify-center">
            <p className="tw-m-0 tw-font-plusJakartaSans tw-font-medium tw-text-lg tw-leading-[22.68px] tw-text-[#6C6C6C] max-nine:tw-text-sm tw-flex tw-items-center tw-gap-2">
              {translate(AuthenticationEnum.dont_have_an_account)}{' '}
              <DefaultLink
                href="/auth/sign-up"
                className="tw-text-main tw-underline tw-underline-offset-4"
              >
                {translate(AuthenticationEnum.sign_up)}
              </DefaultLink>
            </p>
          </div>
        </div>
      </AuthCommonLayout>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = getSignInServerSideProps;

SignIn.getLayout = function getLayout(page: ReactElement) {
  return <>{page}</>;
};
